import {Landregisterfolio} from "../model/landregisterfolio";
import {Property} from "../model/property";
import {Flur} from "../model/flur";
import {Flurstueck} from "../model/flurstueck";

export class LandregisterfolioUtils {

  /**
   * Updates the provided landregisterfolios array with form values.
   * If there are more form numbers than folios, the extras are appended
   * to the first folio as a comma-separated string.
   *
   * @param folios Target array of Landregisterfolio objects
   * @param formValues Array of objects containing at least a 'number' field
   */
  public static assignFolioNumbers(folios: Landregisterfolio[], formValues: Array<{ number: string }>): void {
    if (!folios) {
      return
    }
    const firstFolio = folios[0];

    formValues.forEach((formValue, i) => {
      const trimmedNumber = formValue?.number?.trim() ?? '';
      if (!trimmedNumber) {
        return;
      }

      if (folios[i]) {
        // Assign number directly if folio at index exists
        folios[i].number = trimmedNumber;
      } else if (firstFolio && trimmedNumber) {
        // Fallback: append to first folio’s number, comma-separated
        firstFolio.number = firstFolio.number ? `${firstFolio.number},${trimmedNumber}` : trimmedNumber;
      }
    });
  }

  /**
   * Copies folios of a saved property for use in a new order, leaving out all ids.
   *
   * The source folios belong to a saved property and their ids are those of the
   * saved_landregisterfolio rows. An order stores its folios in its own landregisterfolio table, so
   * passing those ids along makes the backend look for rows that do not exist there. Copying also
   * keeps edits made inside the order from mutating the saved property.
   *
   * @param folios Folios of the saved property
   * @return New folio objects carrying only the values, without ids
   */
  public static copyForNewOrder(folios: Landregisterfolio[]): Landregisterfolio[] {
    return (folios ?? []).map(folio => {
      const folioCopy = new Landregisterfolio();
      folioCopy.number = folio.number;
      folioCopy.gemarkung = folio.gemarkung;
      folioCopy.flure = (folio.flure ?? []).map(flur => {
        const flurCopy = new Flur();
        flurCopy.number = flur.number;
        flurCopy.flurstuecke = (flur.flurstuecke ?? []).map(flurstueck => {
          const flurstueckCopy = new Flurstueck();
          flurstueckCopy.number = flurstueck.number;
          flurstueckCopy.geometry = flurstueck.geometry;
          return flurstueckCopy;
        });
        return flurCopy;
      });
      return folioCopy;
    });
  }

  /**
   * Ensures that the provided property has at least one land register folio.
   *
   * @param property Property object for which folios should be ensured
   */
  public static createFoliosIfMissing(property: Property ): void {
    if (!property.landregisterfolios) {
      property.landregisterfolios = [];
    }

    if (property.landregisterfolios.length === 0) {
      property.landregisterfolios.push(new Landregisterfolio());
    }
  }
}
